import { Menu, Sparkles } from "lucide-react";
import { useCallback, useRef } from "react";
import { setActivePage } from "../app/activePage.ts";
import { useAuthStore } from "../app/authStore.ts";
import { getShortcutLabel } from "../app/keymap.ts";
import { clearSessionState } from "../app/persistence.ts";
import {
  RENDER_MODE_LABELS,
  useActiveRenderConfig,
  useActiveRenderScope,
  VIEW_MODES_BY_SCOPE,
} from "../app/renderConfig.ts";
import { saveSceneToCloudLibrary } from "../app/saveToLibrary.ts";
import { useStore } from "../app/store.ts";
import { useFileLoader } from "../app/useFileLoader.ts";
import { IconButton } from "./Button.tsx";
import {
  showConfirmDialog,
  showSaveOrDiscardDialog,
} from "./ConfirmDialog.tsx";
import { openExportMeshModal } from "./ExportMeshModal.tsx";
import { MenuItem } from "./menu/MenuItem.tsx";
import { MenuButtonRow, MenuDivider } from "./menu/MenuPrimitives.tsx";
import { MenuCheckRow, MenuRadioRow } from "./menu/MenuRadioRow.tsx";
import { SubmenuRow } from "./menu/SubmenuRow.tsx";
import { Popover } from "./Popover.tsx";
import { useSceneStats } from "./StatusBar.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_GOLD,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  FONT_SIZE_TINY,
} from "./tokens.ts";

export function HamburgerMenu({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const isTrial = useAuthStore((s) => s.storageUsage?.tier === "unsubscribed");
  const { focalLength, objectCount, faceCount, vertCount } = useSceneStats();
  const resetScene = useStore((s) => s.resetScene);
  const debugEnabled = useStore((s) => s.debugEnabled);
  const setDebugEnabled = useStore((s) => s.setDebugEnabled);
  const sceneDirty = useStore((s) => s.sceneDirty);
  const newScene = useStore((s) => s.newScene);
  const saveSceneToFile = useStore((s) => s.saveSceneToFile);
  const loadSceneFromFile = useStore((s) => s.loadSceneFromFile);
  const hasBlendFile = useStore((s) => s.blendFiles.size > 0);
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);

  const handleFile = useFileLoader();

  // File input refs
  const blendInputRef = useRef<HTMLInputElement>(null);
  const prInputRef = useRef<HTMLInputElement>(null);

  const handleNewScene = useCallback(async () => {
    if (
      sceneDirty &&
      !(await showConfirmDialog(
        "You have unsaved changes. Discard and create a new scene?",
      ))
    )
      return;
    newScene();
    onToggle();
  }, [newScene, onToggle, sceneDirty]);

  const handleOpenScene = useCallback(() => {
    prInputRef.current?.click();
  }, []);

  const handlePrFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        if (
          sceneDirty &&
          !(await showConfirmDialog(
            "You have unsaved changes. Discard and open this scene?",
          ))
        ) {
          e.target.value = "";
          return;
        }
        await loadSceneFromFile(file);
        onToggle();
      }
      e.target.value = "";
    },
    [loadSceneFromFile, onToggle, sceneDirty],
  );

  const handleLoadBlend = useCallback(() => {
    blendInputRef.current?.click();
  }, []);

  const handleBlendFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
        onToggle();
      }
      e.target.value = "";
    },
    [handleFile, onToggle],
  );

  const handleSaveToLibrary = useCallback(async () => {
    await saveSceneToCloudLibrary();
    onToggle();
  }, [onToggle]);

  const handleDownload = useCallback(async () => {
    await saveSceneToFile(true);
    onToggle();
  }, [saveSceneToFile, onToggle]);

  const handleBackToLibrary = useCallback(async () => {
    if (sceneDirty) {
      const result = await showSaveOrDiscardDialog();
      if (result === "cancel") return;
      if (result === "save") {
        useStore.getState().setLoading(true);
        let ok = false;
        try {
          ok = await saveSceneToCloudLibrary();
        } finally {
          useStore.getState().setLoading(false);
        }
        if (!ok) return;
      }
      if (result === "discard") {
        clearSessionState();
      }
    }
    setActivePage("library");
    onToggle();
  }, [sceneDirty, onToggle]);

  const handleReset = useCallback(() => {
    resetScene();
    onToggle();
  }, [resetScene, onToggle]);

  const handleExportMesh = useCallback(() => {
    openExportMeshModal();
    onToggle();
  }, [onToggle]);

  const renderScope = useActiveRenderScope();
  const activeViewMode = useActiveRenderConfig((c) => c.viewMode);
  const setActiveRenderSetting = useStore((s) => s.setActiveRenderSetting);
  const setRenderMethodModalOpen = useStore((s) => s.setRenderMethodModalOpen);

  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <div style={{ position: "relative", pointerEvents: "auto" }}>
      <IconButton
        ref={btnRef}
        onClick={onToggle}
        icon={<Menu size={18} />}
        label="Project"
        size={32}
        style={{
          border: open
            ? `1.5px solid ${COLOR_ACCENT}`
            : "1.5px solid transparent",
          background: open ? COLOR_ACTIVE_BG : "none",
          color: open ? COLOR_ACCENT : COLOR_TEXT_FAINT,
          opacity: open ? 1 : 0.8,
          transition:
            "border-color 0.15s ease, color 0.15s ease, background 0.15s ease",
        }}
      />

      <Popover
        anchorRef={btnRef}
        open={open}
        onClose={onToggle}
        style={{ minWidth: 240 }}
      >
        {isTrial ? (
          <MenuItem
            label="Account Settings…"
            onClick={() => {
              setActivePage("accountSettings");
              onToggle();
            }}
          />
        ) : (
          <MenuItem
            label="Back to Library"
            shortcut={getShortcutLabel("backToLibrary")}
            onClick={handleBackToLibrary}
          />
        )}
        <MenuDivider />
        <MenuItem
          label="New Scene"
          shortcut={getShortcutLabel("newScene")}
          onClick={handleNewScene}
        />
        <MenuDivider />
        <MenuItem
          label="Open Scene (.pr)…"
          shortcut={getShortcutLabel("openScene")}
          onClick={handleOpenScene}
        />
        <MenuItem
          label="Open Blender File (.blend)…"
          onClick={handleLoadBlend}
        />
        <MenuDivider />
        {isTrial ? (
          <MenuItem
            label="Save (.pr)…"
            shortcut={getShortcutLabel("saveScene")}
            onClick={handleDownload}
          />
        ) : (
          <>
            <MenuItem
              label="Save to Library"
              shortcut={getShortcutLabel("saveScene")}
              onClick={handleSaveToLibrary}
            />
            <MenuItem label="Download (.pr)…" onClick={handleDownload} />
          </>
        )}
        <MenuDivider />
        <MenuItem
          label={hasSelection ? "Export Selection…" : "Export Scene…"}
          onClick={handleExportMesh}
        />
        <MenuDivider />
        <MenuItem
          label="Reset to Blend Defaults"
          onClick={handleReset}
          disabled={!hasBlendFile}
        />
        <MenuDivider />
        <SubmenuRow
          label="Render Method"
          indicator={RENDER_MODE_LABELS[activeViewMode]}
        >
          {VIEW_MODES_BY_SCOPE[renderScope].map((m, i) => (
            <MenuRadioRow
              key={m}
              label={RENDER_MODE_LABELS[m]}
              selected={activeViewMode === m}
              shortcut={
                i === 0 ? getShortcutLabel("cycleRenderMethod") : undefined
              }
              onClick={() => setActiveRenderSetting("viewMode", m)}
            />
          ))}
          <MenuDivider />
          <MenuItem
            label="Settings…"
            onClick={() => {
              setRenderMethodModalOpen(true);
              onToggle();
            }}
          />
        </SubmenuRow>
        <MenuDivider />
        <MenuCheckRow
          label="Debug Overlay"
          checked={debugEnabled}
          onClick={() => setDebugEnabled(!debugEnabled)}
        />
        <MenuDivider />
        <MenuItem
          label="Settings…"
          shortcut={getShortcutLabel("toggleSettings")}
          onClick={() => {
            useStore.getState().setSettingsOpen(true);
            onToggle();
          }}
        />
        {isTrial && (
          <>
            <MenuDivider />
            <MenuButtonRow
              onClick={() => {
                setActivePage("accountSettings");
                onToggle();
              }}
              style={{
                color: COLOR_GOLD,
                gap: 7,
                background: "rgba(240,192,64,0.06)",
              }}
            >
              <Sparkles size={13} />
              <span>Subscribe to unlock library</span>
            </MenuButtonRow>
          </>
        )}
        <MenuDivider />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px 2px",
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_FAINT,
            flexWrap: "wrap",
          }}
        >
          <span>{focalLength}mm</span>
          <span style={{ color: COLOR_TEXT_DISABLED }}>&middot;</span>
          <span>{objectCount} objects</span>
          <span style={{ color: COLOR_TEXT_DISABLED }}>&middot;</span>
          <span>{faceCount.toLocaleString()} faces</span>
          <span style={{ color: COLOR_TEXT_DISABLED }}>&middot;</span>
          <span>{vertCount.toLocaleString()} verts</span>
        </div>
      </Popover>

      {/* Hidden file inputs */}
      <input
        ref={blendInputRef}
        type="file"
        accept=".blend"
        onChange={handleBlendFileChange}
        style={{ display: "none" }}
      />
      <input
        ref={prInputRef}
        type="file"
        accept=".pr"
        onChange={handlePrFileChange}
        style={{ display: "none" }}
      />
    </div>
  );
}
